import React, { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  FiBarChart2,
  FiChevronDown,
  FiClock,
  FiCopy,
  FiEdit3,
  FiEye,
  FiFilter,
  FiList,
  FiMail,
  FiPlus,
  FiPower,
  FiSend,
  FiTrash2,
  FiZap,
} from "react-icons/fi";
import {
  createRosterAutomation,
  deleteRosterAutomation,
  getRosterAutomationMetadata,
  getRosterAutomations,
  testRosterAutomation,
  updateRosterAutomation,
} from "../../../api/roster";

const defaultAction = {
  type: "send_email",
  recipient: "performer",
  to: "",
  subject: "thanks for sharing availability with {{rosterName}}",
  body: "Hi {{performerName}},\n\nThanks for sharing your availability for {{rosterName}}. We will review it and follow up soon.",
};

const defaultForm = {
  name: "",
  description: "",
  trigger: "roster.invite.availability_submitted",
  enabled: true,
  delayMinutes: 0,
  conditions: [],
  actions: [defaultAction],
};

const triggerCopyByValue = {
  "roster.invite.sent": {
    name: "invite follow-up",
    subject: "your invite from {{businessName}}",
    body: "Hi,\n\n{{businessName}} invited you to share availability for {{rosterName}}. Use this link when you are ready: {{inviteUrl}}",
  },
  "roster.invite.availability_submitted": {
    name: "availability confirmation",
    subject: "availability received for {{rosterName}}",
    body: "Hi {{performerName}},\n\nThanks for sharing your availability for {{rosterName}}. We will review it and follow up soon.",
  },
  "slot.application.created": {
    name: "application confirmation",
    subject: "application received for {{slotTitle}}",
    body: "Hi {{performerName}},\n\nWe received your application for {{slotTitle}} at {{eventName}}.",
  },
  "slot.application.accepted": {
    name: "slot accepted",
    subject: "you are confirmed for {{eventName}}",
    body: "Hi {{performerName}},\n\nYou are confirmed for {{slotTitle}} at {{eventName}}.",
  },
  "slot.application.declined": {
    name: "slot update",
    subject: "update on {{eventName}}",
    body: "Hi {{performerName}},\n\nThanks for your interest in {{slotTitle}} at {{eventName}}. We are going another direction for this slot.",
  },
};

const conditionPathOptions = [
  { value: "performer.accountType", label: "performer account type" },
  { value: "performer.email", label: "performer email" },
  { value: "event.name", label: "event name" },
  { value: "slot.title", label: "slot title" },
  { value: "application.note", label: "application note" },
  { value: "invite.email", label: "invite email" },
];

const conditionOperators = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "exists", label: "exists" },
];

const viewTabs = [
  { id: "automations", label: "automations", Icon: FiList },
  { id: "builder", label: "builder", Icon: FiZap },
  { id: "history", label: "history", Icon: FiBarChart2 },
];

const inputClass =
  "w-full rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-16 py-kit-12 text-kit-sm text-[var(--inyc-primary-text-color)] outline-none transition-colors duration-150 focus:border-[var(--inyc-accent)]";

const runStatusClass = {
  sent: "bg-success-soft text-success",
  scheduled: "bg-info-soft text-info",
  skipped: "bg-neutral-soft text-neutral",
  failed: "bg-danger-soft text-danger",
};

const sampleVariables = {
  rosterName: "comedy night",
  performerName: "Jordan Lee",
  performerEmail: "artist@example.com",
  eventName: "Friday showcase",
  slotTitle: "Opener",
  applicationNote: "Available after 8pm.",
  inviteEmail: "artist@example.com",
  inviteUrl: "https://in-yc.com/#/join-roster",
  bookingsUrl: "https://in-yc.com/#/dashboard?tab=bookings",
  rosterUrl: "https://in-yc.com/#/dashboard?tab=bookings",
  eventsUrl: "https://in-yc.com/#/",
  businessName: "Venue name",
};

const cloneForm = (source = defaultForm) => ({
  name: source.name || "",
  description: source.description || "",
  trigger: source.trigger || defaultForm.trigger,
  enabled: source.enabled !== false,
  delayMinutes: source.delayMinutes || 0,
  conditions: Array.isArray(source.conditions) ? source.conditions.map((item) => ({ ...item })) : [],
  actions: source.actions?.length ? source.actions.map((item) => ({ ...defaultAction, ...item })) : [defaultAction],
});

const getRuleId = (rule) => String(rule?._id || rule || "");
const getRunRuleId = (run) => String(run?.rule?._id || run?.rule || "");

const formatDateTime = (value) => {
  if (!value) return "not yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "not yet";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const previewText = (text = "") =>
  String(text).replace(/\{\{([^}]+)\}\}/g, (_, key) => sampleVariables[key.trim()] || `{{${key}}}`);

const RosterAutomationsWorkspace = ({ listId }) => {
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [runPage, setRunPage] = useState({ page: 1, limit: 20, total: 0, hasMore: false });
  const [metadata, setMetadata] = useState({ triggers: [], variables: [], recipients: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeView, setActiveView] = useState("automations");
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [savedSnapshot, setSavedSnapshot] = useState(JSON.stringify(defaultForm));
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    status: "all",
    ruleId: "all",
    trigger: "all",
    page: 1,
  });

  const action = form.actions[0] || defaultAction;
  const selectedRule = useMemo(
    () => rules.find((rule) => rule._id === selectedRuleId),
    [rules, selectedRuleId]
  );
  const selectedTrigger = useMemo(
    () => metadata.triggers.find((trigger) => trigger.value === form.trigger),
    [metadata.triggers, form.trigger]
  );
  const isDirty = useMemo(
    () => JSON.stringify(cloneForm(form)) !== savedSnapshot,
    [form, savedSnapshot]
  );
  const latestRunByRule = useMemo(() => {
    const map = new Map();
    runs.forEach((run) => {
      const id = getRunRuleId(run);
      if (id && !map.has(id)) map.set(id, run);
    });
    return map;
  }, [runs]);
  const stats = useMemo(
    () => ({
      total: rules.length,
      active: rules.filter((rule) => rule.enabled !== false).length,
      disabled: rules.filter((rule) => rule.enabled === false).length,
      failed: runs.filter((run) => run.status === "failed").length,
    }),
    [rules, runs]
  );

  const fetchAutomations = async (filters = historyFilters) => {
    setLoading(true);
    try {
      const [meta, data] = await Promise.all([
        getRosterAutomationMetadata(),
        getRosterAutomations(listId, {
          status: filters.status,
          ruleId: filters.ruleId,
          trigger: filters.trigger,
          page: filters.page,
          limit: 20,
        }),
      ]);
      setMetadata(meta);
      setRules(data.rules || []);
      setRuns(data.runs || []);
      setRunPage(data.runPage || { page: filters.page, limit: 20, total: data.runs?.length || 0, hasMore: false });
    } catch {
      enqueueSnackbar("Failed to load automations", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
  }, [listId, historyFilters.status, historyFilters.ruleId, historyFilters.trigger, historyFilters.page]);

  const confirmDiscard = () => !isDirty || window.confirm("Discard unsaved automation changes?");

  const openView = (view) => {
    if (view !== "builder" && activeView === "builder" && !confirmDiscard()) return;
    setActiveView(view);
  };

  const loadForm = (nextForm, ruleId = null) => {
    const cloned = cloneForm(nextForm);
    setSelectedRuleId(ruleId);
    setForm(cloned);
    setSavedSnapshot(JSON.stringify(cloned));
    setActiveView("builder");
  };

  const createNew = () => {
    if (activeView === "builder" && !confirmDiscard()) return;
    loadForm(defaultForm);
  };

  const editRule = (rule) => loadForm(rule, rule._id);

  const duplicateRule = (rule) => {
    const copy = cloneForm(rule);
    copy.name = `${copy.name || "automation"} copy`;
    loadForm(copy);
  };

  const updateAction = (patch) => {
    setForm((prev) => ({
      ...prev,
      actions: [{ ...defaultAction, ...prev.actions[0], ...patch }],
    }));
  };

  const handleTriggerChange = (trigger) => {
    const copy = triggerCopyByValue[trigger];
    setForm((prev) => ({
      ...prev,
      trigger,
      name: selectedRule ? prev.name : copy?.name || prev.name,
      actions: [
        {
          ...defaultAction,
          ...prev.actions[0],
          subject: copy?.subject || prev.actions[0]?.subject,
          body: copy?.body || prev.actions[0]?.body,
        },
      ],
    }));
  };

  const addCondition = () => {
    setForm((prev) => ({
      ...prev,
      conditions: [
        ...(prev.conditions || []),
        { path: conditionPathOptions[0].value, operator: "equals", value: "" },
      ],
    }));
  };

  const updateCondition = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).map((condition, itemIndex) =>
        itemIndex === index ? { ...condition, ...patch } : condition
      ),
    }));
  };

  const removeCondition = (index) => {
    setForm((prev) => ({
      ...prev,
      conditions: (prev.conditions || []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const insertVariable = (field, variable) => {
    updateAction({ [field]: `${action[field] || ""} {{${variable}}}`.trim() });
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Name is required";
    if (!action.subject?.trim()) return "Email subject is required";
    if (!action.body?.trim()) return "Email body is required";
    if (action.recipient === "custom" && !action.to?.trim()) return "Custom email is required";
    return "";
  };

  const saveRule = async () => {
    const error = validateForm();
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = cloneForm(form);
      const data = selectedRule
        ? await updateRosterAutomation(listId, selectedRule._id, payload)
        : await createRosterAutomation(listId, payload);
      setRules((prev) => {
        const exists = prev.some((rule) => rule._id === data.rule._id);
        return exists
          ? prev.map((rule) => (rule._id === data.rule._id ? data.rule : rule))
          : [data.rule, ...prev];
      });
      loadForm(data.rule, data.rule._id);
      enqueueSnackbar("Automation saved", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to save automation", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    try {
      const data = await updateRosterAutomation(listId, rule._id, {
        ...cloneForm(rule),
        enabled: rule.enabled === false,
      });
      setRules((prev) => prev.map((item) => (item._id === data.rule._id ? data.rule : item)));
    } catch {
      enqueueSnackbar("Failed to update automation", { variant: "error" });
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Delete "${rule.name}"?`)) return;
    try {
      await deleteRosterAutomation(listId, rule._id);
      setRules((prev) => prev.filter((item) => item._id !== rule._id));
      if (selectedRuleId === rule._id) loadForm(defaultForm);
      enqueueSnackbar("Automation deleted", { variant: "info" });
    } catch {
      enqueueSnackbar("Failed to delete automation", { variant: "error" });
    }
  };

  const sendTest = async (ruleInput = form) => {
    const error = validateForm();
    if (activeView === "builder" && error) {
      enqueueSnackbar(error, { variant: "error" });
      return;
    }

    try {
      await testRosterAutomation(listId, cloneForm(ruleInput));
      enqueueSnackbar("Test email sent", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error || "Failed to send test", { variant: "error" });
    }
  };

  const updateHistoryFilter = (patch) => {
    setHistoryFilters((prev) => ({ ...prev, ...patch, page: patch.page || 1 }));
  };

  if (loading) {
    return (
      <div className="py-kit-32 text-center text-[var(--inyc-primary-text-color-light)]">
        loading automations...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-kit-16">
      <section className="overflow-hidden rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)]">
        <div className="relative p-kit-16 md:p-kit-20">
          <div className="absolute right-[-3rem] top-[-4rem] h-[9rem] w-[9rem] rounded-kit-full bg-[var(--inyc-secondary)] opacity-[0.08]" />
          <div className="relative flex flex-col gap-kit-16 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-kit-8 font-['Primary-Bold'] text-kit-base">
                <FiZap size={17} />
                email automations
              </div>
              <p className="mt-kit-6 max-w-[34rem] text-kit-xs leading-[1.45] text-[var(--inyc-primary-text-color-light)]">
                Build roster-specific responses for invites, availability, and lineup decisions.
              </p>
            </div>
            <button
              type="button"
              onClick={createNew}
              className="flex w-fit items-center gap-kit-6 rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-8 text-kit-xs font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)]"
            >
              <FiPlus size={14} />
              new automation
            </button>
          </div>

          <div className="relative mt-kit-16 grid grid-cols-2 gap-kit-8 sm:grid-cols-4">
            {[
              ["total", stats.total],
              ["active", stats.active],
              ["disabled", stats.disabled],
              ["failed runs", stats.failed],
            ].map(([label, value]) => (
              <div key={label} className="rounded-kit-lg bg-[var(--inyc-primary)] px-kit-12 py-kit-10">
                <div className="font-['Primary-Bold'] text-kit-md">{value}</div>
                <div className="text-kit-2xs lowercase text-[var(--inyc-primary-text-color-light)]">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-kit-6 overflow-x-auto border-t-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-6">
          {viewTabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => openView(id)}
              className={`flex min-w-fit items-center gap-kit-6 rounded-kit-full px-kit-12 py-kit-8 text-kit-xs transition-colors duration-150 ${
                activeView === id
                  ? "bg-[var(--inyc-secondary)] font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)]"
                  : "text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]"
              }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </section>

      {activeView === "automations" && (
        <section className="grid gap-kit-12">
          {rules.length === 0 ? (
            <div className="rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-24 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
              no automations yet. create one from the builder to start.
            </div>
          ) : (
            rules.map((rule) => {
              const trigger = metadata.triggers.find((item) => item.value === rule.trigger);
              const latestRun = latestRunByRule.get(getRuleId(rule));
              return (
                <article
                  key={rule._id}
                  className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16"
                >
                  <div className="flex flex-col gap-kit-12 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-kit-8">
                        <h3 className="truncate font-['Primary-Bold'] text-kit-base">{rule.name}</h3>
                        <span className={`rounded-kit-full px-kit-8 py-kit-2 text-kit-2xs ${
                          rule.enabled ? "bg-success-soft text-success" : "bg-gray-100 text-gray-500"
                        }`}>
                          {rule.enabled ? "on" : "off"}
                        </span>
                      </div>
                      <div className="mt-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                        {trigger?.label || rule.trigger} · {rule.delayMinutes || 0} min delay
                      </div>
                      {rule.description && (
                        <p className="mt-kit-8 line-clamp-2 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                          {rule.description}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-kit-6">
                      <button type="button" onClick={() => editRule(rule)} className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-10 py-kit-6 text-kit-2xs">
                        <FiEdit3 className="mr-kit-4 inline" size={12} />
                        edit
                      </button>
                      <button type="button" onClick={() => duplicateRule(rule)} className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-10 py-kit-6 text-kit-2xs">
                        <FiCopy className="mr-kit-4 inline" size={12} />
                        duplicate
                      </button>
                      <button type="button" onClick={() => sendTest(rule)} className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-10 py-kit-6 text-kit-2xs">
                        <FiSend className="mr-kit-4 inline" size={12} />
                        test
                      </button>
                      <button type="button" onClick={() => toggleRule(rule)} className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-10 py-kit-6 text-kit-2xs">
                        <FiPower className="mr-kit-4 inline" size={12} />
                        {rule.enabled ? "disable" : "enable"}
                      </button>
                      <button type="button" onClick={() => removeRule(rule)} className="rounded-kit-full border-[1px] border-danger-line px-kit-10 py-kit-6 text-kit-2xs text-danger">
                        <FiTrash2 className="mr-kit-4 inline" size={12} />
                        delete
                      </button>
                    </div>
                  </div>
                  <div className="mt-kit-12 rounded-kit-md bg-[var(--inyc-primary)] px-kit-12 py-kit-10 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                    last run: {latestRun ? `${latestRun.status} · ${formatDateTime(latestRun.executedAt || latestRun.scheduledFor || latestRun.createdAt)}` : "none yet"}
                  </div>
                </article>
              );
            })
          )}
        </section>
      )}

      {activeView === "builder" && (
        <section className="grid grid-cols-1 gap-kit-16 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16">
            <div className="mb-kit-16 flex items-center justify-between gap-kit-16">
              <div>
                <h3 className="font-['Primary-Bold'] text-kit-base">{selectedRule ? "edit automation" : "new automation"}</h3>
                <p className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">Choose the trigger, filters, timing, and email.</p>
              </div>
              {isDirty && <span className="rounded-kit-full bg-warning-soft px-kit-10 py-kit-4 text-kit-2xs text-warning">unsaved</span>}
            </div>

            <div className="flex flex-col gap-kit-16">
              <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                name
                <input className={inputClass} value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="availability confirmation" />
              </label>

              <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                description
                <input className={inputClass} value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="when this should send" />
              </label>

              <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                trigger
                <select className={inputClass} value={form.trigger} onChange={(e) => handleTriggerChange(e.target.value)}>
                  {metadata.triggers.map((trigger) => (
                    <option key={trigger.value} value={trigger.value}>{trigger.label}</option>
                  ))}
                </select>
                {selectedTrigger?.description && <span className="text-kit-2xs">{selectedTrigger.description}</span>}
              </label>

              <div className="rounded-kit-lg bg-[var(--inyc-primary)] p-kit-12">
                <div className="mb-kit-8 flex items-center justify-between gap-kit-16">
                  <div>
                    <div className="font-['Primary-Medium'] text-kit-sm">conditions</div>
                    <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">optional filters before this automation runs</div>
                  </div>
                  <button type="button" onClick={addCondition} className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-10 py-kit-6 text-kit-2xs">
                    add condition
                  </button>
                </div>
                {(form.conditions || []).length === 0 ? (
                  <div className="rounded-kit-md border-[1px] border-dashed border-[var(--inyc-input-border-color)] p-kit-12 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                    no conditions - this runs any time the trigger happens
                  </div>
                ) : (
                  <div className="flex flex-col gap-kit-8">
                    {form.conditions.map((condition, index) => (
                      <div key={`${condition.path}-${index}`} className="grid gap-kit-8 md:grid-cols-[1fr_0.8fr_1fr_auto]">
                        <select className={inputClass} value={condition.path} onChange={(e) => updateCondition(index, { path: e.target.value })}>
                          {conditionPathOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <select className={inputClass} value={condition.operator} onChange={(e) => updateCondition(index, { operator: e.target.value })}>
                          {conditionOperators.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <input
                          className={inputClass}
                          value={condition.value || ""}
                          disabled={condition.operator === "exists"}
                          onChange={(e) => updateCondition(index, { value: e.target.value })}
                          placeholder={condition.operator === "exists" ? "no value needed" : "value"}
                        />
                        <button type="button" onClick={() => removeCondition(index)} className="rounded-kit-full border-[1px] border-danger-line px-kit-10 text-kit-2xs text-danger">
                          remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-kit-12 md:grid-cols-2">
                <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                  recipient
                  <select className={inputClass} value={action.recipient} onChange={(e) => updateAction({ recipient: e.target.value })}>
                    {metadata.recipients.map((recipient) => (
                      <option key={recipient.value} value={recipient.value}>{recipient.label}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                  delay
                  <div className="relative">
                    <FiClock className="absolute left-[0.8rem] top-1/2 -translate-y-1/2 text-[var(--inyc-primary-text-color-light)]" size={14} />
                    <input className={`${inputClass} pl-kit-32`} type="number" min="0" value={form.delayMinutes} onChange={(e) => setForm((prev) => ({ ...prev, delayMinutes: e.target.value }))} />
                  </div>
                  <span className="text-kit-2xs">minutes after trigger. 0 sends immediately.</span>
                </label>
              </div>

              {action.recipient === "custom" && (
                <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                  custom email
                  <input className={inputClass} value={action.to} onChange={(e) => updateAction({ to: e.target.value })} placeholder="email@example.com" />
                </label>
              )}

              <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                subject
                <input className={inputClass} value={action.subject} onChange={(e) => updateAction({ subject: e.target.value })} />
              </label>

              <label className="flex flex-col gap-kit-4 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
                body
                <textarea className={`${inputClass} min-h-[12rem] resize-y leading-relaxed`} value={action.body} onChange={(e) => updateAction({ body: e.target.value })} />
              </label>

              <label className="flex items-center gap-kit-8 text-kit-xs">
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))} />
                automation is enabled
              </label>

              <div className="flex flex-wrap gap-kit-10 pt-kit-4">
                <button type="button" onClick={saveRule} disabled={saving} className="rounded-kit-full bg-[var(--inyc-secondary)] px-kit-16 py-kit-10 text-kit-xs font-['Primary-Medium'] text-[var(--inyc-secondary-text-color)] disabled:opacity-60">
                  {saving ? "saving..." : "save automation"}
                </button>
                <button type="button" onClick={() => sendTest(form)} className="flex items-center gap-kit-6 rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-16 py-kit-10 text-kit-xs">
                  <FiSend size={13} />
                  send test
                </button>
                {selectedRule && (
                  <button type="button" onClick={() => removeRule(selectedRule)} className="ml-auto flex items-center gap-kit-6 rounded-kit-full border-[1px] border-danger-line px-kit-16 py-kit-10 text-kit-xs text-danger">
                    <FiTrash2 size={13} />
                    delete
                  </button>
                )}
              </div>
            </div>
          </div>

          <aside className="flex flex-col gap-kit-16">
            <div className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16">
              <div className="mb-kit-10 flex items-center gap-kit-8 font-['Primary-Bold'] text-kit-sm">
                <FiEye size={15} />
                email preview
              </div>
              <div className="rounded-kit-lg bg-[var(--inyc-primary)] p-kit-16">
                <div className="text-kit-2xs text-[var(--inyc-primary-text-color-light)]">subject</div>
                <div className="mt-kit-4 font-['Primary-Medium'] text-kit-sm">{previewText(action.subject) || "no subject yet"}</div>
                <div className="mt-kit-12 whitespace-pre-wrap text-kit-xs leading-[1.55] text-[var(--inyc-primary-text-color-light)]">
                  {previewText(action.body) || "write the body to preview it here"}
                </div>
              </div>
            </div>

            <div className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16">
              <div className="mb-kit-8 font-['Primary-Bold'] text-kit-sm">variables</div>
              <div className="mb-kit-10 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">Add placeholders without typing braces.</div>
              <div className="flex flex-wrap gap-kit-6">
                {metadata.variables.map((variable) => (
                  <button
                    key={variable}
                    type="button"
                    onClick={() => insertVariable("body", variable)}
                    className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] px-kit-8 py-kit-4 text-kit-2xs text-[var(--inyc-primary-text-color-light)]"
                  >
                    {variable}
                  </button>
                ))}
              </div>
              <div className="mt-kit-12 flex flex-wrap gap-kit-6">
                {metadata.variables.slice(0, 5).map((variable) => (
                  <button
                    key={`subject-${variable}`}
                    type="button"
                    onClick={() => insertVariable("subject", variable)}
                    className="rounded-kit-full bg-[var(--inyc-primary)] px-kit-8 py-kit-4 text-kit-2xs text-[var(--inyc-primary-text-color-light)]"
                  >
                    add {variable} to subject
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </section>
      )}

      {activeView === "history" && (
        <section className="rounded-kit-lg border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary-light)] p-kit-16">
          <div className="mb-kit-16 flex flex-col gap-kit-12 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-kit-8 font-['Primary-Bold'] text-kit-base">
                <FiFilter size={15} />
                run history
              </div>
              <p className="text-kit-xs text-[var(--inyc-primary-text-color-light)]">Inspect sent, scheduled, skipped, and failed automation runs.</p>
            </div>
            <div className="grid grid-cols-1 gap-kit-8 sm:grid-cols-3">
              <select className={inputClass} value={historyFilters.status} onChange={(e) => updateHistoryFilter({ status: e.target.value })}>
                <option value="all">all statuses</option>
                {Object.keys(runStatusClass).map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select className={inputClass} value={historyFilters.ruleId} onChange={(e) => updateHistoryFilter({ ruleId: e.target.value })}>
                <option value="all">all automations</option>
                {rules.map((rule) => (
                  <option key={rule._id} value={rule._id}>{rule.name}</option>
                ))}
              </select>
              <select className={inputClass} value={historyFilters.trigger} onChange={(e) => updateHistoryFilter({ trigger: e.target.value })}>
                <option value="all">all triggers</option>
                {metadata.triggers.map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>{trigger.label}</option>
                ))}
              </select>
            </div>
          </div>

          {runs.length === 0 ? (
            <div className="rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] p-kit-24 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
              no runs match these filters
            </div>
          ) : (
            <div className="flex flex-col gap-kit-8">
              {runs.map((run) => {
                const expanded = expandedRunId === run._id;
                return (
                  <article key={run._id} className="rounded-kit-lg bg-[var(--inyc-primary)] p-kit-12">
                    <button
                      type="button"
                      onClick={() => setExpandedRunId(expanded ? null : run._id)}
                      className="flex w-full items-center justify-between gap-kit-12 text-left"
                    >
                      <div className="min-w-0">
                        <div className="truncate font-['Primary-Medium'] text-kit-sm">
                          {run.rule?.name || metadata.triggers.find((item) => item.value === run.trigger)?.label || run.trigger}
                        </div>
                        <div className="mt-kit-2 truncate text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                          {run.recipient || "no recipient"} · {formatDateTime(run.executedAt || run.scheduledFor || run.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-kit-8">
                        <span className={`rounded-kit-full px-kit-8 py-kit-2 text-kit-2xs ${runStatusClass[run.status] || "bg-gray-100 text-gray-600"}`}>
                          {run.status}
                        </span>
                        <FiChevronDown className={`transition-transform duration-150 ${expanded ? "rotate-180" : ""}`} size={14} />
                      </div>
                    </button>
                    {expanded && (
                      <div className="mt-kit-12 grid gap-kit-10 text-kit-2xs text-[var(--inyc-primary-text-color-light)]">
                        <div>trigger: {run.trigger}</div>
                        <div>scheduled: {formatDateTime(run.scheduledFor)}</div>
                        <div>executed: {formatDateTime(run.executedAt)}</div>
                        {run.error && <div className="rounded-kit-md bg-danger-soft p-kit-10 text-danger">error: {run.error}</div>}
                        <details>
                          <summary className="cursor-pointer font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">action snapshot</summary>
                          <pre className="mt-kit-8 max-h-[14rem] overflow-auto rounded-kit-md bg-[var(--inyc-primary-light)] p-kit-10 text-kit-2xs">
                            {JSON.stringify(run.actionSnapshot || {}, null, 2)}
                          </pre>
                        </details>
                        <details>
                          <summary className="cursor-pointer font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">payload</summary>
                          <pre className="mt-kit-8 max-h-[14rem] overflow-auto rounded-kit-md bg-[var(--inyc-primary-light)] p-kit-10 text-kit-2xs">
                            {JSON.stringify(run.payload || {}, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          <div className="mt-kit-16 flex items-center justify-between gap-kit-16 text-kit-xs text-[var(--inyc-primary-text-color-light)]">
            <span>
              page {runPage.page} · {runPage.total} runs
            </span>
            <div className="flex gap-kit-8">
              <button
                type="button"
                disabled={historyFilters.page <= 1}
                onClick={() => updateHistoryFilter({ page: historyFilters.page - 1 })}
                className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-12 py-kit-6 disabled:opacity-40"
              >
                previous
              </button>
              <button
                type="button"
                disabled={!runPage.hasMore}
                onClick={() => updateHistoryFilter({ page: historyFilters.page + 1 })}
                className="rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] px-kit-12 py-kit-6 disabled:opacity-40"
              >
                next
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default RosterAutomationsWorkspace;
